'use client';

import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import Desktop from './responsive/desktop';
import Mobile from './responsive/mobile';
import { IPoint } from '@/_store/common/types';
import { IRedux } from '@/_store/types';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  onCloseMap,
  savePoint,
  setDeliveryZone,
  updatePoint,
} from '@/_store/common/common.slice';
import { toaster } from '@/components/ui/toaster';
import { useTranslations } from 'next-intl';
import { sessionActions } from '@/_store/session/session.slice';
import { IDeliveryZone } from '@/_types';
import customerService from '@/_service/customer.service';
import { useQueryClient } from '@tanstack/react-query';
import { useActiveBrand } from '@/_hooks/useActiveBrand';

export interface AddressDetailsRef {
  saveAddress: () => void;
}

export type SetDeliveryZoneFn = (zone: { id: string; min_order_amount: number } | null) => void;

export interface IProps {
  city: string;
  coords: number[];
  address: string;
  deliveryZone: IDeliveryZone | { id: string; min_order_amount: number } | null;
  setDeliveryZone?: SetDeliveryZoneFn;
  accommodation: string;
  onClear: () => void;
  isLoading?: boolean;
  errorZone?: boolean;
  isDesktop: boolean;
}

const initial: IPoint = {
  name: '',
  address: '',
  floor: '',
  apartment: '',
  accommodation: '',
  to_address_description: '',
  location: [0, 0],
  isActive: true,
  id: '',
  city: '',
  building: '',
};

const AddressDetails = forwardRef<AddressDetailsRef, IProps>(
  (
    {
      city,
      coords,
      address,
      accommodation,
      onClear,
      isLoading,
      errorZone,
      deliveryZone,
      setDeliveryZone: setDeliveryZoneProp,
      isDesktop,
    },
    ref
  ) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const shipperId = useActiveBrand();
    const { editingPoint } = useSelector((state: IRedux) => state.common);
    const { user } = useSelector((state: IRedux) => state.auth);

    const [formData, setFormData] = useState(initial);

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    // Clear the address input field
    const clearInput = () => {
      setFormData((prev) => ({ ...prev, name: '' }));
    };

    const saveAddress = useCallback(async () => {
      if (coords?.length !== 2) {
        return toaster.create({
          title: t('location.has_not_been_detected'),
          description: t('please_enter_address'),
          type: 'error',
        });
      }

      const data = {
        ...formData,
        city,
        location: coords,
        id: formData?.id ? formData?.id : uuidv4(),
      };

      if (setDeliveryZoneProp) {
        if (deliveryZone?.id) {
          setDeliveryZoneProp({
            id: deliveryZone.id,
            min_order_amount: deliveryZone.min_order_amount,
          });
        } else {
          setDeliveryZoneProp(null);
        }
      } else if (deliveryZone?.id && shipperId) {
        dispatch(
          setDeliveryZone({
            shipper_id: shipperId,
            zone: {
              id: deliveryZone.id,
              min_order_amount: deliveryZone.min_order_amount,
            },
          })
        );
      } else if (shipperId) {
        dispatch(setDeliveryZone({ shipper_id: shipperId, zone: null }));
      }

      if (!editingPoint && user?.id) {
        try {
          await customerService.createCustomerAddress({
            address: data.address || '',
            apartment: data.apartment || '',
            building: data.building || '',
            customer_id: user.id,
            description: data.to_address_description || '',
            floor: data.floor || '',
            location: {
              lat: coords[0],
              long: coords[1],
            },
            name: data.name || data.address || t('address'),
          });
          // Invalidate customer addresses query to refetch the list
          queryClient.invalidateQueries({
            queryKey: ['customer-addresses'],
          });
        } catch (error) {
          toaster.create({
            title: t('error_occured'),
            type: 'error',
          });
          return;
        }
      }

      if (editingPoint) {
        // Check if this is a customer address from API
        const isCustomerAddress = (editingPoint as any)?.isCustomerAddress;
        const customerAddressId = (editingPoint as any)?.customerAddressId;

        if (isCustomerAddress && customerAddressId && user?.id) {
          try {
            await customerService.updateCustomerAddress(customerAddressId, {
              address: data.address || '',
              apartment: data.apartment || '',
              building: data.building || '',
              customer_id: user.id,
              description: data.to_address_description || '',
              floor: data.floor || '',
              location: {
                lat: coords[0],
                long: coords[1],
              },
              name: data.name || data.address || t('address'),
            });
            // Invalidate customer addresses query to refetch the list
            queryClient.invalidateQueries({
              queryKey: ['customer-addresses'],
            });
            toaster.create({
              title: t('address_successfully_updated'),
              type: 'success',
            });
          } catch (error) {
            toaster.create({
              title: t('error_occured'),
              type: 'error',
            });
            return;
          }
        } else {
          dispatch(updatePoint(data));
          toaster.create({
            title: t('address_successfully_updated'),
            type: 'success',
          });
        }
      } else {
        dispatch(sessionActions.acceptPGL());
        dispatch(savePoint(data));
        toaster.create({
          title: t('address_successfully_added'),
          type: 'success',
        });
      }

      setFormData(initial);
      dispatch(onCloseMap());

      // there was router.refresh after 500ms
      onClear();
    }, [
      coords,
      formData,
      city,
      deliveryZone,
      editingPoint,
      dispatch,
      t,
      onClear,
      user?.id,
      queryClient,
    ]);

    useEffect(() => {
      setFormData((prev) => ({ ...prev, address, accommodation }));
    }, [address, accommodation]);

    useEffect(() => {
      if (editingPoint) {
        setFormData(editingPoint);
      }
    }, [editingPoint]);

    useImperativeHandle(
      ref,
      () => ({
        saveAddress,
      }),
      [saveAddress]
    );

    if (isDesktop) {
      return (
        <Desktop
          formData={formData}
          clearInput={clearInput}
          handleInputChange={handleInputChange}
        />
      );
    }

    return (
      <Mobile
        city={city}
        address={address}
        formData={formData}
        clearInput={clearInput}
        saveAddress={saveAddress}
        handleInputChange={handleInputChange}
      />
    );
  }
);

AddressDetails.displayName = 'AddressDetails';

export default AddressDetails;
